import Keycloak from 'keycloak-js';
import axios from 'axios';

// Initialize Keycloak instance
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8484/',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'lode',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'lode-frontend'
};

const keycloak = new Keycloak(keycloakConfig);

// Set up axios interceptor for adding auth token
const setupAxiosInterceptors = () => {
    axios.interceptors.request.use(
      (config) => {
        console.log('Intercepting request:', config.url);
        if (keycloak.token) {
          console.log('Adding auth token'); 
          config.headers.Authorization = `Bearer ${keycloak.token}`;
        } else {
          console.warn('No token available');
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor to debug issues
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('Response error:', error);
        if (error.response) {
          console.error('Status:', error.response.status);
          console.error('Headers:', error.response.headers);
        }
        return Promise.reject(error);
      }
    );
  };

// Initialize Keycloak
const initKeycloak = (onAuthenticatedCallback) => {
  keycloak.init({
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: window.location.origin + '/lode/silent-check-sso.html',
    pkceMethod: 'S256',
    checkLoginIframe: false
  })
    .then((authenticated) => {
      if (authenticated) {
        setupAxiosInterceptors();
        
        // Store tokens in sessionStorage for persistence
        sessionStorage.setItem('keycloak-token', keycloak.token);
        sessionStorage.setItem('keycloak-refresh-token', keycloak.refreshToken);
        
        // Set up token refresh
        setInterval(() => {
          keycloak.updateToken(70)
            .then(() => {
              sessionStorage.setItem('keycloak-token', keycloak.token);
              sessionStorage.setItem('keycloak-refresh-token', keycloak.refreshToken);
            })
            .catch(() => {
              console.error('Failed to refresh token');
              sessionStorage.removeItem('keycloak-token');
              sessionStorage.removeItem('keycloak-refresh-token');
            });
        }, 60000);
      } else {
        // Try to restore from sessionStorage
        const storedToken = sessionStorage.getItem('keycloak-token');
        const storedRefreshToken = sessionStorage.getItem('keycloak-refresh-token');
        
        if (storedToken && storedRefreshToken) {
          keycloak.token = storedToken;
          keycloak.refreshToken = storedRefreshToken;
          
          // Parse the token to populate tokenParsed
          try {
            const base64Payload = storedToken.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            keycloak.tokenParsed = payload;
            console.log('Token restored and parsed successfully');
          } catch (e) {
            console.error('Failed to parse stored token:', e);
          }
          
          setupAxiosInterceptors();
          
          // Set up token refresh for restored session
          setInterval(() => {
            keycloak.updateToken(70)
              .then(() => {
                sessionStorage.setItem('keycloak-token', keycloak.token);
                sessionStorage.setItem('keycloak-refresh-token', keycloak.refreshToken);
              })
              .catch(() => {
                console.error('Failed to refresh token');
                sessionStorage.removeItem('keycloak-token');
                sessionStorage.removeItem('keycloak-refresh-token');
              });
          }, 60000);
        }
      }
      onAuthenticatedCallback();
    })
    .catch(error => {
      console.error('Failed to initialize Keycloak', error);
      onAuthenticatedCallback();
    });
};

const doLogin = () => keycloak.login();
const doLogout = () => {
  // Clear stored tokens
  sessionStorage.removeItem('keycloak-token');
  sessionStorage.removeItem('keycloak-refresh-token');
  return keycloak.logout({ redirectUri: window.location.origin + '/lode/' });
};
const getToken = () => keycloak.token;
const isLoggedIn = () => !!keycloak.token;
const getUsername = () => keycloak.tokenParsed?.preferred_username;
const hasRole = (roles) => {
  if (keycloak.resourceAccess) {
    const userRoles = keycloak.resourceAccess[keycloakConfig.clientId]?.roles || [];
    return Array.isArray(roles)
      ? roles.some(role => userRoles.includes(role))
      : userRoles.includes(roles);
  }
  return false;
};
const getFirstName = () => keycloak.tokenParsed?.given_name;
const getLastName = () => keycloak.tokenParsed?.family_name;
const getEmail = () => keycloak.tokenParsed?.email;

export {
  initKeycloak,
  doLogin,
  doLogout,
  getToken,
  isLoggedIn,
  getUsername,
  getFirstName,
  getLastName,
  getEmail,
  hasRole
};